import { useEffect, type Dispatch, type RefObject, type SetStateAction } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { elowenClient } from '../../lib/elowenClient';
import { BRAIN_COMPOSE_EVENT, BRAIN_OPEN_EVENT, type BrainOpenRequest } from '../../lib/brainDock';
import { subscribeRevive } from '../../lib/useRevive';
import type { StreamSilenceLimits } from '../../lib/streamWatchdog';
import type { BrainChatStream } from './brainChatStream';

interface BrainChatLifecycleOptions {
  onOpenRef: RefObject<(request: BrainOpenRequest | undefined) => void>;
  onComposeRef: RefObject<(text: string | undefined) => void>;
  attachedRef: RefObject<boolean>;
  clientId: () => string;
  setActivityWakeVersion: Dispatch<SetStateAction<number>>;
  stream: BrainChatStream;
  silence: StreamSilenceLimits;
  silenceRef: RefObject<StreamSilenceLimits>;
}

/** Provider-lifetime browser subscriptions. Surface and route changes never detach the conversation. */
export function useBrainChatLifecycle({
  onOpenRef, onComposeRef, attachedRef, clientId,
  setActivityWakeVersion, stream, silence, silenceRef,
}: BrainChatLifecycleOptions): void {
  const qc = useQueryClient();
  useEffect(() => {
    const onOpen = (e: Event) => onOpenRef.current((e as CustomEvent<BrainOpenRequest>).detail);
    const onCompose = (e: Event) => onComposeRef.current((e as CustomEvent<{ text?: string }>).detail?.text);
    window.addEventListener(BRAIN_OPEN_EVENT, onOpen);
    window.addEventListener(BRAIN_COMPOSE_EVENT, onCompose);
    return () => {
      window.removeEventListener(BRAIN_OPEN_EVENT, onOpen);
      window.removeEventListener(BRAIN_COMPOSE_EVENT, onCompose);
    };
  }, [onOpenRef, onComposeRef]);

  // pagehide cannot distinguish a closed tab from a phone lock with an open terminal WebSocket.
  // Never stop its binding here: transport detach preserves it for GET recovery, and the daemon's
  // existing 30-minute idle reaper collects abandoned runtimes instead of an unload mutation.

  // Report whether this tab is on screen. The daemon holds an SSE stream open behind a locked phone, so
  // being attached says nothing about whether the answer is being read — without this the "turn finished"
  // push assumes someone is watching and never fires. Also reported on every (re)attach, because a
  // reconnect mints a fresh stream that starts out assumed-visible: a tab that reconnects while hidden
  // (the daemon's own revive path does exactly that) would otherwise silence the notification again.
  useEffect(() => {
    const report = (): void => {
      if (!attachedRef.current) return;
      const hidden = document.hidden;
      elowenClient.brainVisibility({ client: clientId(), hidden });
      if (!hidden) {
        setActivityWakeVersion((value) => value + 1);
        void qc.invalidateQueries({ queryKey: ['brain-sessions'] });
      }
    };
    // `pagehide` is the one event iOS fires reliably when an installed PWA goes to the background or the
    // phone locks; `visibilitychange` alone can be skipped there, which left the daemon believing the
    // conversation was still being read. `pageshow` is its counterpart on the way back, because a page
    // restored from the bfcache does not re-run this effect.
    const hide = (): void => { if (attachedRef.current) elowenClient.brainVisibility({ client: clientId(), hidden: true }); };
    report();
    document.addEventListener('visibilitychange', report);
    window.addEventListener('pagehide', hide);
    window.addEventListener('pageshow', report);
    return () => {
      document.removeEventListener('visibilitychange', report);
      window.removeEventListener('pagehide', hide);
      window.removeEventListener('pageshow', report);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One foreground return or network recovery may renew a stale/exhausted GET episode.
  // Focus and watchdog signals retain its finite budget; no wake replays brainStart.
  useEffect(() => subscribeRevive(event => {
    // Through a ref, because this subscription is made once: a limit edited mid-session must reach the next
    // wake-up without re-subscribing (which would drop the wake-ups that arrive while React re-runs it).
    stream.revive(event, attachedRef.current, silenceRef.current.reviveLimitMs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  // A dropped reader can stay pending without an error or EOF. All received bytes, including heartbeat
  // comments, prove liveness to the watchdog; only a fully applied snapshot proves application readiness.
  // Unlike the wake-up path this one is re-armed when the operator changes the limit, since the interval
  // reads it once at start; the watchdog holds no state worth preserving across that restart.
  // Stream methods only read refs; re-running for the hook object's render-time identity would reset the timer.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => stream.watch(() => attachedRef.current, silence.limitMs), [silence.limitMs]);

  // Tear the stream down when the whole provider unmounts (app teardown), matching today's cleanup.
  // Stream stop only reads refs; this cleanup intentionally belongs to the provider lifetime, not each render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => () => stream.stop(), []);

}
